import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { addArtist, isAuthenticated } from '../../services/artistService';
import { compressImage } from '../../services/imageService';
import { SectionHeader } from '../../components/ui/SectionHeader';
import { ChevronLeft, Save, Loader2, ChevronDown, Trash2 } from 'lucide-react';

export const AddArtist: React.FC = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        genre: '',
        bio: '',
        image: '',
        instagram: '',
        spotify: '',
        soundcloud: '',
        spotifyId: '',
        epk: ''
    });

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/admin/login');
        }
    }, [navigate]);

    const [isCompressing, setIsCompressing] = useState(false);
    const [isGenreDropdownOpen, setIsGenreDropdownOpen] = useState(false);
    const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
    const [customGenre, setCustomGenre] = useState('');
    const [isOtherSelected, setIsOtherSelected] = useState(false);

    const GENRES = [
        'Afro House',
        'Afro Tech',
        'House',
        'Latin House',
        'Mainstage',
        'Melodic House',
        'Melodic Techno',
        'Tribal Rave'
    ];

    const updateFormDataGenre = (genres: string[], other: boolean, custom: string) => {
        const all = [...genres];
        if (other && custom) {
            all.push(custom);
        }
        setFormData(prev => ({ ...prev, genre: all.join(' / ') }));
    };

    const toggleGenre = (genre: string) => {
        let newGenres;
        if (selectedGenres.includes(genre)) {
            newGenres = selectedGenres.filter(g => g !== genre);
        } else {
            newGenres = [...selectedGenres, genre].sort();
        }
        setSelectedGenres(newGenres);
        updateFormDataGenre(newGenres, isOtherSelected, customGenre);
    };

    const toggleOther = () => {
        const newState = !isOtherSelected;
        setIsOtherSelected(newState);
        updateFormDataGenre(selectedGenres, newState, customGenre);
    };

    const handleCustomGenreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setCustomGenre(val);
        updateFormDataGenre(selectedGenres, isOtherSelected, val);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        
        if (name === 'spotify') {
            // Auto-extract Spotify ID
            const match = value.match(/artist\/([a-zA-Z0-9]+)/);
            if (match) {
                setFormData(prev => ({ ...prev, [name]: value, spotifyId: match[1] }));
            } else {
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsCompressing(true);
        try {
            // Compress image to under 1MB (safe for localStorage)
            const compressedImage = await compressImage(file, 1);
            setFormData(prev => ({ ...prev, image: compressedImage }));
        } catch (error) {
            console.error('Error compressing image:', error);
            alert('Failed to process image. Please try another file.');
        } finally {
            setIsCompressing(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // Basic validation
        if (!formData.name || !formData.genre || !formData.bio || !formData.image) {
            alert('Please fill in all required fields (Name, Genre, Bio, Image URL)');
            return;
        }

        try {
            await addArtist(formData);
            navigate('/admin/dashboard');
        } catch (error) {
            alert('Failed to add artist. Please try again.');
        }
    };

    return (
        <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                <button
                    onClick={() => navigate('/admin/dashboard')}
                    className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
                >
                    <ChevronLeft size={16} /> Back to Dashboard
                </button>

                <div className="bg-black/50 border border-white/10 rounded-xl p-8 backdrop-blur-md">
                    <SectionHeader title="Add New Artist" subtitle="Expand the Roster" />

                    <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Name */}
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-sm font-medium text-gray-300 mb-1">Artist Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    className="w-full bg-black/80 border border-white/20 rounded-md p-3 text-white focus:border-astral-purple focus:outline-none"
                                    placeholder="e.g. Lunar Echo"
                                />
                            </div>

                            {/* Genre */}
                            <div className="col-span-2 md:col-span-1 relative">
                                <label className="block text-sm font-medium text-gray-300 mb-1">Genre *</label>
                                <button
                                    type="button"
                                    onClick={() => setIsGenreDropdownOpen(!isGenreDropdownOpen)}
                                    className="w-full bg-black/80 border border-white/20 rounded-md p-3 text-left text-white focus:border-astral-purple focus:outline-none flex justify-between items-center"
                                >
                                    <span className={formData.genre ? 'text-white' : 'text-gray-400'}>
                                        {formData.genre || 'Select Genres...'}
                                    </span>
                                    <ChevronDown size={16} />
                                </button>
                                
                                {isGenreDropdownOpen && (
                                    <div className="absolute z-10 mt-1 w-full bg-black border border-white/20 rounded-md shadow-lg max-h-60 overflow-auto p-2">
                                        {GENRES.map(genre => (
                                            <label key={genre} className="flex items-center gap-2 p-2 hover:bg-white/10 rounded cursor-pointer text-white">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedGenres.includes(genre)}
                                                    onChange={() => toggleGenre(genre)}
                                                    className="rounded border-gray-600 text-astral-purple focus:ring-astral-purple bg-black/50"
                                                />
                                                {genre}
                                            </label>
                                        ))}
                                        <div className="border-t border-white/10 my-1"></div>
                                        <label className="flex items-center gap-2 p-2 hover:bg-white/10 rounded cursor-pointer text-white">
                                            <input
                                                type="checkbox"
                                                checked={isOtherSelected}
                                                onChange={toggleOther}
                                                className="rounded border-gray-600 text-astral-purple focus:ring-astral-purple bg-black/50"
                                            />
                                            Other
                                        </label>
                                    </div>
                                )}
                                
                                {isOtherSelected && (
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            value={customGenre}
                                            onChange={handleCustomGenreChange}
                                            className="w-full bg-black/80 border border-white/20 rounded-md p-3 text-white focus:border-astral-purple focus:outline-none text-sm"
                                            placeholder="Type custom genres..."
                                        />
                                    </div>
                                )}
                                <p className="text-xs text-gray-500 mt-1">Multiple selection allowed.</p>
                            </div>

                            {/* Image Upload */}
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-300 mb-1">Artist Image *</label>
                                <div className="flex flex-col gap-4">
                                    {/* Preview */}
                                    {formData.image && (
                                        <div className="relative w-32 h-32 rounded-md overflow-hidden border border-white/20">
                                            <img 
                                                src={formData.image} 
                                                alt="Preview" 
                                                className="w-full h-full object-cover"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                                                className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 hover:bg-red-500 transition-colors"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    )}

                                    <div className="flex items-center gap-4">
                                        <label className={`flex items-center justify-center px-4 py-2 bg-white/10 text-white rounded-md cursor-pointer hover:bg-white/20 transition-colors border border-white/20 ${isCompressing ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                            <span className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                                                {isCompressing ? <Loader2 className="animate-spin w-4 h-4" /> : 'Choose File'}
                                            </span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                                className="hidden"
                                                disabled={isCompressing}
                                            />
                                        </label>
                                        <span className="text-sm text-gray-400">
                                            {isCompressing ? 'Compressing...' : (formData.image ? 'Image ready' : 'No file chosen')}
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500">Upload a JPG, PNG, or WEBP image. Max size 5MB.</p>
                                </div>
                            </div>

                            {/* Bio */}
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-300 mb-1">Biography *</label>
                                <textarea
                                    name="bio"
                                    value={formData.bio}
                                    onChange={handleChange}
                                    required
                                    rows={5}
                                    className="w-full bg-black/80 border border-white/20 rounded-md p-3 text-white focus:border-astral-purple focus:outline-none"
                                    placeholder="Artist biography..."
                                />
                            </div>

                            {/* Spotify ID */}
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-sm font-medium text-gray-300 mb-1">Spotify Artist ID</label>
                                <input
                                    type="text"
                                    name="spotifyId"
                                    value={formData.spotifyId}
                                    onChange={handleChange}
                                    className="w-full bg-black/80 border border-white/20 rounded-md p-3 text-white focus:border-astral-purple focus:outline-none"
                                    placeholder="e.g. 4Z8W4fKeB5YxbusRsdQVPb"
                                />
                                <p className="text-xs text-gray-500 mt-1">Found in the Spotify Artist URL.</p>
                            </div>

                            {/* Social Links */}
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-sm font-medium text-gray-300 mb-1">Spotify Profile URL</label>
                                <input
                                    type="url"
                                    name="spotify"
                                    value={formData.spotify}
                                    onChange={handleChange}
                                    className="w-full bg-black/80 border border-white/20 rounded-md p-3 text-white focus:border-astral-purple focus:outline-none"
                                    placeholder="https://open.spotify.com/artist/..."
                                />
                            </div>

                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-sm font-medium text-gray-300 mb-1">SoundCloud URL</label>
                                <input
                                    type="url"
                                    name="soundcloud"
                                    value={formData.soundcloud}
                                    onChange={handleChange}
                                    className="w-full bg-black/80 border border-white/20 rounded-md p-3 text-white focus:border-astral-purple focus:outline-none"
                                    placeholder="https://soundcloud.com/..."
                                />
                            </div>

                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-sm font-medium text-gray-300 mb-1">Instagram URL</label>
                                <input
                                    type="url"
                                    name="instagram"
                                    value={formData.instagram}
                                    onChange={handleChange}
                                    className="w-full bg-black/80 border border-white/20 rounded-md p-3 text-white focus:border-astral-purple focus:outline-none"
                                    placeholder="https://instagram.com/..."
                                />
                            </div>
                            
                             <div className="col-span-2 md:col-span-1">
                                <label className="block text-sm font-medium text-gray-300 mb-1">EPK URL</label>
                                <input
                                    type="url"
                                    name="epk"
                                    value={formData.epk}
                                    onChange={handleChange}
                                    className="w-full bg-black/80 border border-white/20 rounded-md p-3 text-white focus:border-astral-purple focus:outline-none"
                                    placeholder="Link to PDF or Dropbox"
                                />
                            </div>
                        </div>

                        <div className="pt-6 border-t border-white/10 flex justify-end">
                            <button
                                type="submit"
                                className="flex items-center gap-2 px-8 py-3 bg-astral-purple text-white font-bold uppercase tracking-wider rounded-md hover:bg-astral-purple/80 transition-all shadow-lg hover:shadow-astral-purple/30"
                            >
                                <Save size={18} /> Save Artist
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
